/**
 * FeatureStageBadge - Visual indicator for feature lifecycle stage
 *
 * Displays a small badge showing Alpha / Beta / RC status (GA shows none).
 * The short label keeps chips compact and aligned; the full stage name is
 * exposed on hover via `title` so "RC" is discoverable.
 * Used in Platform Admin UI, sidebar navigation, and tabs.
 */

import { STAGE_LABELS, STAGE_FULL_LABELS, type FeatureStage } from '../services/api/platformAdmin'
import './FeatureStageBadge.css'

interface FeatureStageBadgeProps {
  stage: FeatureStage
  size?: 'sm' | 'md'
}

const STAGE_CONFIG: Record<FeatureStage, { label: string; className: string }> = {
  alpha: { label: STAGE_LABELS.alpha, className: 'ff-stage-alpha' },
  beta: { label: STAGE_LABELS.beta, className: 'ff-stage-beta' },
  rc: { label: STAGE_LABELS.rc, className: 'ff-stage-rc' },
  ga: { label: '', className: '' }, // "Live"/stable shows no badge (default)
}

export function FeatureStageBadge({ stage, size = 'sm' }: FeatureStageBadgeProps) {
  const config = STAGE_CONFIG[stage]

  // Guard against unknown/legacy stage values (e.g. a pre-rename "uat" row still
  // coming from the API) so a stray value never crashes the whole tree.
  if (!config || !config.label) {
    return null
  }

  return (
    <span
      className={`ff-stage-badge ff-stage-badge--${size} ${config.className}`}
      title={STAGE_FULL_LABELS[stage]}
    >
      {config.label}
    </span>
  )
}
